import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  specimenMacroService,
  specimenBatchTemplateService,
  SpecimenMacro,
  SpecimenBatchTemplate,
  CreateSpecimenMacroInput,
  UpdateSpecimenMacroInput,
  CreateSpecimenBatchTemplateInput,
  UpdateSpecimenBatchTemplateInput,
} from "../../services/admin/specimenMacro.service";

// === SPECIMEN MACROS ===

export function useSpecimenMacrosAdmin() {
  return useQuery<SpecimenMacro[]>({
    queryKey: ["admin", "specimen-macros"],
    queryFn: () => specimenMacroService.getAll(),
  });
}

export function useCreateSpecimenMacro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSpecimenMacroInput) =>
      specimenMacroService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specimen-macros"] });
      queryClient.invalidateQueries({ queryKey: ["specimen-macros"] });
      notifications.show({
        title: "Success",
        message: "Specimen macro created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to create specimen macro";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}

export function useUpdateSpecimenMacro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSpecimenMacroInput) =>
      specimenMacroService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specimen-macros"] });
      queryClient.invalidateQueries({ queryKey: ["specimen-macros"] });
      notifications.show({
        title: "Success",
        message: "Specimen macro updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update specimen macro";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}

export function useToggleSpecimenMacroActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => specimenMacroService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "specimen-macros"] });
      queryClient.invalidateQueries({ queryKey: ["specimen-macros"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update status";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}

// === SPECIMEN BATCH TEMPLATES ===

export function useSpecimenBatchTemplatesAdmin() {
  return useQuery<SpecimenBatchTemplate[]>({
    queryKey: ["admin", "specimen-batch-templates"],
    queryFn: () => specimenBatchTemplateService.getAll(),
  });
}

export function useCreateSpecimenBatchTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSpecimenBatchTemplateInput) =>
      specimenBatchTemplateService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "specimen-batch-templates"],
      });
      queryClient.invalidateQueries({
        queryKey: ["specimen-batch-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Batch template created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to create batch template";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}

export function useUpdateSpecimenBatchTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateSpecimenBatchTemplateInput) =>
      specimenBatchTemplateService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "specimen-batch-templates"],
      });
      queryClient.invalidateQueries({
        queryKey: ["specimen-batch-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Batch template updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update batch template";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}

export function useToggleSpecimenBatchTemplateActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => specimenBatchTemplateService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "specimen-batch-templates"],
      });
      queryClient.invalidateQueries({
        queryKey: ["specimen-batch-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to update status";
      notifications.show({ title: "Error", message, color: "red" });
    },
  });
}
